import { useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { sendMessageToParent } from 'ui-addon-automation-embed/embedUtils';
import PostMessageTypes from 'ui-addon-automation-embed/PostMessageTypes';
import LoadingActionConfig from 'automation-ui-action-features/components/LoadingActionConfig';
import { SIDE_PANEL_EXPANDED_WIDTH, SIDE_PANEL_FULLSCREEN_WIDTH, SIDE_PANEL_WIDTH } from 'automation-ui-core/constants/ui';
import { wireAuthConsumer } from 'automation-ui-core/context/AuthContext';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import { DataPanelVisibleStates } from 'automation-ui-formula-editor/data-passing-panel/DataPassingContext';
import ErrorBoundary from 'automation-ui-canvas/components/errors/ErrorBoundary';
import ErrorPanel from 'automation-ui-canvas/components/errors/ErrorPanel';
import ConfigPanel from 'automation-ui-canvas/components/panels/ConfigPanel';
import ScrollToElementDecorator from 'automation-ui-canvas/decorators/ScrollToElementDecorator';
import { getEmbedId, getIsEmbedded } from 'automation-ui-canvas/selectors/embedSelectors';
import { getSidebarWidthForDataPanelState } from 'automation-ui-canvas/components/panels/utils/getSidebarWidthForDataPanelState';
import { jsx as _jsx } from "react/jsx-runtime";
const FlowConfigsContainer = Loadable({
  loader: () => import( /* webpackChunkName: "flow-configs" */'automation-ui-canvas/containers/FlowConfigsContainer').then(module => module.default),
  LoadingComponent: LoadingActionConfig,
  ErrorComponent: ({
    error
  }) => {
    reportError({
      error
    });
    return /*#__PURE__*/_jsx(ErrorPanel, {});
  }
});
export const preloadFlowConfig = FlowConfigsContainer.preload;
const FlowConfigsContainerAsync = props => {
  const {
    embedId,
    isEmbedded,
    canvasScrollElementRef
  } = props;
  const [isFullScreen, setIsFullScreen] = useState(false);
  // we start out with the panel closed. It gets opened from the actionConfig children
  // through the handleActionConfigDataPanelDisplayChange method/prop.
  const [panelWidth, setPanelWidth] = useState(SIDE_PANEL_WIDTH);
  const [actionConfigDataPanelDisplay, setActionConfigDataPanelDisplay] = useState(DataPanelVisibleStates.CLOSED);
  const handlePanelWidthChange = newPanelWidth => {
    setPanelWidth(isFullScreen ? SIDE_PANEL_FULLSCREEN_WIDTH : newPanelWidth);
    if (embedId) {
      sendMessageToParent({
        type: PostMessageTypes.RESIZE_SIDE_PANEL,
        width: isFullScreen ? SIDE_PANEL_FULLSCREEN_WIDTH : `${newPanelWidth}px`,
        embedId
      });
    }
  };
  const handleDataPanelWidthChange = (newDataPanelVisibleState, newPanelWidth) => {
    setActionConfigDataPanelDisplay(newDataPanelVisibleState);
    // DEV NOTE: Support having the data panel regardless of the isFullScreen being active.
    // actionConfigDataPanelDisplay drives how the panel displays. Width drives how wide
    // the UIPanel is displayed.
    handlePanelWidthChange(newPanelWidth);
  };
  const handleActionConfigDataPanelDisplayChange = newDataPanelVisibleState => {
    handleDataPanelWidthChange(newDataPanelVisibleState, getSidebarWidthForDataPanelState(newDataPanelVisibleState));
  };
  const handleFiltersEditorPanelOpenChange = isOpen => {
    handlePanelWidthChange(isOpen ? SIDE_PANEL_EXPANDED_WIDTH : SIDE_PANEL_WIDTH);
  };
  const renderContentComponents = /*#__PURE__*/_jsx(ErrorBoundary, {
    boundaryName: "FlowConfigsContainer_ErrorPanel",
    renderError: () => /*#__PURE__*/_jsx(ErrorPanel, {}),
    children: /*#__PURE__*/_jsx(FlowConfigsContainer, Object.assign({}, props, {
      isFullScreen: isFullScreen,
      setIsFullScreen: setIsFullScreen,
      onChangeActionConfigDataPanelDisplay: handleActionConfigDataPanelDisplayChange,
      actionConfigDataPanelDisplay: actionConfigDataPanelDisplay,
      onFiltersEditorPanelOpenChange: handleFiltersEditorPanelOpenChange
    }))
  });
  return /*#__PURE__*/_jsx(ConfigPanel, {
    align: "left",
    canvasScrollElementRef: canvasScrollElementRef,
    dataTestId: "flow-configs-container",
    isEmbedded: isEmbedded,
    width: isFullScreen ? SIDE_PANEL_FULLSCREEN_WIDTH : panelWidth,
    children: renderContentComponents
  });
};
export default compose(wireAuthConsumer, ScrollToElementDecorator, connect(state => {
  return {
    embedId: getEmbedId(state),
    isEmbedded: getIsEmbedded(state)
  };
}))(FlowConfigsContainerAsync);